/* eslint-disable react/jsx-key */
import React, { useState, useEffect, useRef, useMemo, SyntheticEvent } from 'react';
import './dashboard.css';
import { DrugExceptionsClient } from '../../../client/drug-exceptions-client';
import { DrugExceptionResponse, DrugExceptionStatus } from '../../../model/Reformulary';
import { Select, TableContainer, TableHead, TableCell, TableSortLabel, MenuItem, Table, Paper, TableRow, TableBody, InputLabel, TablePagination } from '@mui/material';
import { useTable, useSortBy, Cell } from 'react-table';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface HandleError {
    handleError: Function;
}

interface TableData {
    submissionDate: string;
    caseId: number;
    memberName: string;
    claimantName: string;
    assessorName?: string;
    status: string;
}

export default function AdminDashboard(props: HandleError) {
    const history = useNavigate();
    const storedUserId = sessionStorage.getItem('userId');
    const userId = storedUserId ? JSON.parse(storedUserId) : null;
    const drugExceptionsClient = new DrugExceptionsClient();
    const monthList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const [exceptionList, setExceptionList] = useState<DrugExceptionResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);

    function getStatusText(ref: string) {
        let displayString = '';
        switch (ref) {
            case (DrugExceptionStatus[DrugExceptionStatus.UNDER_REVIEW]):
                displayString = 'Under Review';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.APPROVED]):
                displayString = 'Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.INCOMPLETE]):
                displayString = 'Incomplete';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.NOT_APPROVED]):
                displayString = 'Not Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.EXPIRED]):
                displayString = 'Not Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.ASSESSED_APPROVED]):
                displayString = 'Assessed/Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.ASSESSED_NOT_APPROVED]):
                displayString = 'Assessed/Not Approved';
                break;
            default:
                displayString = '';
                break;
        }
        return displayString;
    }

    async function fetchData() {
        setLoading(true);
        let drugExceptionResponse;
        if (userId) {
            const token = userId.authToken;
            const id = userId.id;
            if (token && id) {
                drugExceptionResponse = await drugExceptionsClient.api.get('/drugexceptions', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
            setExceptionList(drugExceptionResponse?.data?.reverse() || []);
        }
        setLoading(false);
    }

    const columns = React.useMemo(
        () => [
            {
                Header: 'Submission Date',
                accessor: 'submissionDate'
            },
            {
                Header: 'Case ID',
                accessor: 'caseId'
            },
            {
                Header: 'Member Name',
                accessor: 'memberName'
            },
            {
                Header: 'Claimant Name',
                accessor: 'claimantName'
            },
            {
                Header: 'Assessor Name',
                accessor: 'assessorName',
            },
            {
                Header: 'Status',
                accessor: 'status'
            }
        ],
        []
    );

    const data = useMemo<TableData[]>(() =>
        exceptionList
            .sort((a: { id: number }, b: { id: number }) => b.id - a.id)
            .map<TableData>((e) => ({
                submissionDate: format(new Date(e.submittedDate), 'MMM dd yyyy'),
                caseId: e.id,
                memberName: `${e.claimant?.memberFirstName} ${e.claimant?.memberLastName}`,
                claimantName: `${e.claimant?.firstName} ${e.claimant?.lastName}`,
                assessorName: e.assessor ? `${e.assessor?.firstName} ${e.assessor?.lastName}` : '',
                status: getStatusText(e.drugExceptionStatus)
            })), [exceptionList]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable<TableData>(
        { columns, data },
        useSortBy
    );

    useEffect(() => {
        fetchData();
    }, []);

    const openExceptionDetails = (event: React.MouseEvent<unknown>, drugExceptionId: number) => {
        const drugException = exceptionList.filter(e => e.id === drugExceptionId);
        sessionStorage.setItem('selectedDrugException', JSON.stringify(drugException[0]));
        console.log(drugException[0]);
        history('/admin/approve');
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // --- Fixed pagination slice ---
    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <div className="admin-dashboard-container">
            <div className="header">
                <h1>DASHBOARD</h1>
                <button className="mdc-button mdc-button--raised mdc-ripple-upgrade button-shaped" onClick={e=>history('/admin/deleteuser')}>
                    <div className="mdc-button__ripple"></div>
                    <span className="mdc-button__label">Delete user</span>
                </button>
                <button className="mdc-button mdc-button--raised mdc-ripple-upgrade button-shaped" onClick={e=>history('/admin/exception')}>
                    <div className="mdc-button__ripple"></div>
                    <span className="mdc-button__label">New exception request</span>
                </button>
            </div>
            <Paper>
                <TableContainer>
                    <Table {...getTableProps()}>
                        <TableHead>
                            {headerGroups.map(headerGroup => (
                                <TableRow {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <TableCell
                                            {...(column.id === 'selection'
                                                ? column.getHeaderProps()
                                                : column.getHeaderProps(column.getSortByToggleProps()))}
                                        >
                                            {column.render('Header')}
                                            {column.id !== 'selection' ? (
                                                <TableSortLabel
                                                    active={column.isSorted}
                                                    direction={column.isSortedDesc ? 'desc' : 'asc'}
                                                />
                                            ) : null}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody {...getTableBodyProps()}>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>Loading...</TableCell>
                                </TableRow>
                            ) : (
                                paginatedRows.map(row => {
                                    prepareRow(row);
                                    return (
                                        <TableRow hover onClick={ (event) => openExceptionDetails(event, row.original.caseId) } {...row.getRowProps()}>
                                            {row.cells.map(cell => (
                                                <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100, 200]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </div>
    );
}
